import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { parseMarkdown } from '../../../utils/markdown';
import Modal from '../Modal';
import ModalButton from '../ModalButton';
import SettingDetails from './SettingDetails';
import { useShowSettingDetails } from './SettingDetailsContext';

/**
 * The "want to know more" affordance that follows a settings row's one-line description.
 *
 * Inside a SettingsModal it pages that modal in place; anywhere else there is nothing to page, so
 * it opens a dialog of its own rather than doing nothing at all.
 */
const DetailsPill = ({ title, details, label }) => {
  const { t } = useTranslation();
  const showDetails = useShowSettingDetails();
  const [isOpen, setIsOpen] = useState(false);

  // A doc that opens with a `# Heading` names itself; without one it takes the name of the row it
  // hangs off. Parsed once here rather than in SettingDetails, because the title is wanted by the
  // modal header and the blocks by the body.
  const { title: docTitle, blocks } = useMemo(() => parseMarkdown(details), [details]);
  const pageTitle = docTitle || title;

  const handleClick = () => {
    if (showDetails) {
      showDetails({ title: pageTitle, blocks });
      return;
    }
    setIsOpen(true);
  };

  return (
    <>
      <button type="button" className="DetailsPill" onClick={handleClick}>
        {label || t('dialogs.details.seeMore')}
      </button>

      {!showDetails && (
        <Modal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          title={pageTitle}
          footer={<ModalButton onClick={() => setIsOpen(false)}>{t('common.gotIt')}</ModalButton>}
        >
          <SettingDetails blocks={blocks} />
        </Modal>
      )}
    </>
  );
};

DetailsPill.propTypes = {
  /** Fallback page title, used when the markdown carries no `# Heading` of its own */
  title: PropTypes.string.isRequired,
  /** The document, as markdown — see src/utils/markdown.js for the syntax that is read */
  details: PropTypes.string.isRequired,
  label: PropTypes.string,
};

export default DetailsPill;
